import type { Tick } from "../types";

export type TradeSide = "buy" | "sell";

export interface ReplayTrade {
  id: string; // backend position id
  symbol: string;
  side: TradeSide;
  lot: number;
  entry: number;
  stopLoss: number | null;
  takeProfit: number | null;
  openedAt: number; // market time (epoch s)
  status: "open" | "closed";
  exit?: number;
  exitTime?: number;
  reason?: "manual" | "sl" | "tp";
  r?: number | null;
  pnl?: number;
  snapshot?: Record<string, unknown> | null;
}

export interface ReplayStats {
  trades: number; wins: number; losses: number; winRate: number;
  netR: number; avgR: number; bestR: number; worstR: number; maxDrawdownR: number; profitFactor: number | null;
  equity: number[]; // cumulative R after each closed trade
}

export function rMultiple(t: ReplayTrade, exit: number): number | null {
  if (t.stopLoss == null) return null;
  const risk = Math.abs(t.entry - t.stopLoss);
  if (risk <= 0) return null;
  return ((exit - t.entry) * (t.side === "buy" ? 1 : -1)) / risk;
}

export function pnl(t: ReplayTrade, exit: number): number {
  return (exit - t.entry) * (t.side === "buy" ? 1 : -1) * t.lot * 100;
}

// Tick-level SL/TP resolution on the replayed stream. Only ticks that already reached the bus are ever seen.
export function resolveOnTick(t: ReplayTrade, tick: Tick): { exit: number; reason: "sl" | "tp" } | null {
  if (t.status !== "open" || tick.time < t.openedAt) return null;
  const p = tick.price;
  if (t.side === "buy") {
    if (t.stopLoss != null && p <= t.stopLoss) return { exit: t.stopLoss, reason: "sl" };
    if (t.takeProfit != null && p >= t.takeProfit) return { exit: t.takeProfit, reason: "tp" };
  } else {
    if (t.stopLoss != null && p >= t.stopLoss) return { exit: t.stopLoss, reason: "sl" };
    if (t.takeProfit != null && p <= t.takeProfit) return { exit: t.takeProfit, reason: "tp" };
  }
  return null;
}

export function computeStats(trades: ReplayTrade[]): ReplayStats {
  const closed = trades.filter((t) => t.status === "closed").sort((a, b) => (a.exitTime ?? 0) - (b.exitTime ?? 0));
  const rs = closed.map((t) => t.r ?? 0);
  const wins = closed.filter((t) => (t.r ?? t.pnl ?? 0) > 0).length;
  const losses = closed.filter((t) => (t.r ?? t.pnl ?? 0) < 0).length;
  const equity: number[] = [];
  let cum = 0, peak = 0, mdd = 0;
  for (const r of rs) { cum += r; equity.push(cum); peak = Math.max(peak, cum); mdd = Math.min(mdd, cum - peak); }
  const gross = rs.filter((r) => r > 0).reduce((a, b) => a + b, 0);
  const loss = -rs.filter((r) => r < 0).reduce((a, b) => a + b, 0);
  return {
    trades: closed.length, wins, losses, winRate: closed.length ? (wins / closed.length) * 100 : 0,
    netR: cum, avgR: rs.length ? cum / rs.length : 0, bestR: rs.length ? Math.max(...rs) : 0, worstR: rs.length ? Math.min(...rs) : 0,
    maxDrawdownR: mdd, profitFactor: loss > 0 ? gross / loss : gross > 0 ? Infinity : null, equity,
  };
}
